import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchForsideSlides } from '../lib/cms';

const DEFAULT_SLIDES = [
  {
    url: 'https://assets.auxodev.dk/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/omos.jpg',
    title: 'Pluk-selv-blomster nær Billund',
    subtitle: 'Kom og besøg Midtjyllands måske hyggeligste gård med pluk-selv-blomster',
    knap_tekst: 'Bestil knolde nu',
    knap_link: '/bestil-knolde'
  },
  {
    url: 'https://assets.auxodev.dk/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/arrangementer.jpg',
    title: 'Køb Dahlia knolde og stiklinger',
    subtitle: 'Stort udvalg af over 200 smukke mexicanske dahlia-sorter til din egen have',
    knap_tekst: 'Bestil knolde nu',
    knap_link: '/bestil-knolde'
  },
  {
    url: 'https://assets.auxodev.dk/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/a215-scaled.jpg',
    title: 'Et hav af former, størrelser og farver',
    subtitle: 'Sammensæt din helt egen unikke buket til hverdag eller fest',
    knap_tekst: 'Bestil knolde nu',
    knap_link: '/bestil-knolde'
  }
];

export default function HeroSlider() {
  const [slides, setSlides] = useState(DEFAULT_SLIDES);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    async function loadSlides() {
      try {
        const data = await fetchForsideSlides();
        if (Array.isArray(data) && data.length > 0) {
          const sorted = [...data].sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
          const formatted = sorted.map((s) => {
            const rawLink = s.knap_link || '';
            const normalizedLink = (!rawLink || rawLink === '#bestillingsliste') ? '/bestil-knolde' : rawLink;
            return {
              url: (s.billede && s.billede[0]) || s.url || '',
              title: s.titel || s.title || '',
              subtitle: s.undertitel || s.subtitle || '',
              knap_tekst: s.knap_tekst || 'Bestil knolde nu',
              knap_link: normalizedLink
            };
          }).filter(s => s.url && s.title);

          if (formatted.length > 0) {
            setSlides(formatted);
          }
        }
      } catch (err) {
        console.error('Fejl ved hentning af slides:', err);
      }
    }
    loadSlides();
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const activeSlide = slides[current] || slides[0] || DEFAULT_SLIDES[0];

  return (
    <div className="relative w-full h-[75vh] sm:h-[65vh] min-h-[520px] sm:min-h-[480px] overflow-hidden bg-forest-950">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            index === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Cloudflare on-the-fly resizing applied here */}
          <img
            src={`https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/${slide.url.replace('https://assets.auxodev.dk/', '')}`}
            alt={slide.title}
            className="w-full h-full object-cover object-center scale-105 animate-[zoom_20s_infinite_alternate]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-forest-950/90 via-forest-950/65 to-forest-950/40" />
        </div>
      ))}

      {/* Content */}
      <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif font-bold text-white leading-tight text-balance">
            {activeSlide.title}
          </h1>
          <p className="mt-6 text-base sm:text-lg md:text-xl text-cream-100 max-w-2xl mx-auto leading-relaxed text-balance">
            {activeSlide.subtitle}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            {activeSlide.knap_link?.startsWith('/') ? (
              <Link
                to={activeSlide.knap_link}
                className="bg-dahlia-500 hover:bg-dahlia-600 text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
              >
                <i className="fa-solid fa-cart-shopping" />
                {activeSlide.knap_tekst || 'Bestil knolde nu'}
              </Link>
            ) : (
              <a
                href={activeSlide.knap_link || '/bestil-knolde'}
                className="bg-dahlia-500 hover:bg-dahlia-600 text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
              >
                <i className="fa-solid fa-cart-shopping" />
                {activeSlide.knap_tekst || 'Bestil knolde nu'}
              </a>
            )}
            <a
              href="https://www.google.dk/maps/dir//Grindstedvej+55,+7184+Vandel"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white hover:bg-cream-100 text-forest-950 px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
            >
              <i className="fa-solid fa-location-dot text-black" />
              Find vej til gården
            </a>
          </div>
        </div>
      </div>

      {/* CSS Animation for Zoom */}
      <style>{`
        @keyframes zoom {
          from { transform: scale(1); }
          to { transform: scale(1.08); }
        }
      `}</style>
    </div>
  );
}
